import FetchError from '@/FetchError'
import { useFetch } from '@/composables/fetch'

const { fetch } = useFetch()

export default abstract class Api {
  protected fetch = fetch

  protected resource = ''

  public async get<T>(options = {}) {
    let url = this.resource

    const params = new URLSearchParams(options).toString()

    if (Object.keys(options).length) {
      url += `?${params}`
    }

    const { data, error, response } = await this.fetch(url).json<T>()

    return this.handleResponse<T>(data.value, error.value, response.value?.status)
  }

  public async paginate<T>(options = {}) {
    const fetchOptions = { ...options, paginate: true }

    try {
      return await this.get<T>(fetchOptions)
    } catch (error) {
      const err = error as FetchError
      throw new FetchError(err.message, err.errors)
    }
  }

  public async show<T>(resourceId: number) {
    const { data, error, response } = await this.fetch(`${this.resource}/${resourceId}`).json<T>()

    return this.handleResponse<T>(data.value, error.value, response.value?.status)
  }

  public async store<T>(options = {}) {
    const { data, error, response } = await this.fetch(this.resource).post(options).json<T>()

    return this.handleResponse<T>(data.value, error.value, response.value?.status)
  }

  public async update<T>(resourceId: number, options = {}) {
    const { data, error, response } = await this.fetch(`${this.resource}/${resourceId}`)
      .put(options)
      .json<T>()

    return this.handleResponse<T>(data.value, error.value, response.value?.status)
  }

  public async destroy<T>(resourceId: number) {
    const { data, error, response } = await this.fetch(`${this.resource}/${resourceId}`)
      .delete()
      .json<T>()

    return this.handleResponse<T>(data.value, error.value, response.value?.status)
  }

  protected handleResponse<T>(
    data: T | null,
    error: { errors: Record<string, string[]> | null; message: string } | null,
    code?: number | null
  ) {
    if (error?.message) {
      throw new FetchError(error.message, error.errors, code)
    }

    return data as T
  }
}
